import { getMessaging, getToken, onMessage } from "firebase/messaging";
import AppConfig from './app-config';
import apiClient from './api-client';

class NotificationService {
  constructor() {
    this.messaging = null;
    this.currentToken = null;
    this.tokenRefreshListener = null;
    this.messagingSwRegistration = null;
    this.visibilityHandlerBound = false;
  }

  async getMessagingServiceWorkerRegistration() {
    if (this.messagingSwRegistration) {
      return this.messagingSwRegistration;
    }

    if (!(typeof window !== 'undefined' && 'serviceWorker' in navigator)) {
      return null;
    }

    try {
      this.messagingSwRegistration = await navigator.serviceWorker.register('/firebase-messaging-sw.js', {
        scope: '/firebase-cloud-messaging-push-scope',
        updateViaCache: 'none'
      });
      return this.messagingSwRegistration;
    } catch (error) {
      console.warn('Failed to register firebase messaging service worker:', error);
      return null;
    }
  }

  /**
   * Initialize Firebase messaging and set up token refresh listener
   */
  async initialize() {
    if (!('Notification' in window)) {
      console.warn('Notifications not supported in this browser');
      return false;
    }

    try {
      this.messaging = getMessaging();
      
      // Set up message listener for foreground notifications
      onMessage(this.messaging, (payload) => {
        console.log('Foreground notification received:', payload);
        this.handleForegroundNotification(payload);
      });

      // Check if we have an existing token and it's still valid
      const storedTokenId = AppConfig.getLocal('notification_token_id');
      if (storedTokenId) {
        // Token exists, set up refresh monitoring
        this.setupTokenRefreshMonitoring();
      }

      return true;
    } catch (error) {
      console.error('Error initializing notification service:', error);
      return false;
    }
  }

  /**
   * Request notification permission and register token
   */
  async requestPermissionAndRegister() {
    if (!('Notification' in window)) {
      throw new Error('Notifications not supported');
    }

    try {
      const permission = await Notification.requestPermission();
      
      if (permission === 'granted') {
        console.log('Notification permission granted');
        await this.registerToken();
        return true;
      } else if (permission === 'denied') {
        console.log('Notification permission denied');
        return false;
      }
    } catch (error) {
      console.error('Error requesting notification permission:', error);
      throw error;
    }
  }

  /**
   * Register or refresh FCM token with backend
   */
  async registerToken(forceRefresh = false) {
    if (!this.messaging) {
      await this.initialize();
    }

    try {
      // Wait for service worker to be ready
      if ('serviceWorker' in navigator) {
        await navigator.serviceWorker.ready;
      }

      const messagingSwRegistration = await this.getMessagingServiceWorkerRegistration();

      const token = await getToken(this.messaging, { 
        vapidKey: process.env.REACT_APP_VAPID_KEY,
        ...(messagingSwRegistration ? { serviceWorkerRegistration: messagingSwRegistration } : {})
      });

      if (!token) {
        throw new Error('Failed to get FCM token');
      }

      const tokenId = token.slice(-6);
      const storedTokenId = AppConfig.getLocal('notification_token_id');

      // Only register if token is new or force refresh is requested
      if (forceRefresh || tokenId !== storedTokenId) {
        console.log('Registering new token with backend');
        
        const response = await apiClient.post('users/notification/register', {
          token: token,
          platform: AppConfig.getDevicePlatform(),
          device: navigator.userAgent
        }, {}, { action: 'register_notification_token' });

        if (response.status === 200) {
          AppConfig.storeLocal('notification_token_id', tokenId);
          AppConfig.storeLocal('notification_enable', 'true');
          AppConfig.storeLocal('notification_token_timestamp', Date.now());
          this.currentToken = token;
          
          // Set up token refresh monitoring
          this.setupTokenRefreshMonitoring();
          
          console.log('Token registered successfully');
          return true;
        }
      } else {
        console.log('Token already registered');
        this.currentToken = token;
        return true;
      }
    } catch (error) {
      console.error('Error registering token:', error);
      throw error;
    }
  }

  /**
   * Set up monitoring for token refresh
   * FCM tokens can be refreshed by Firebase, we need to detect and update
   */
  setupTokenRefreshMonitoring() {
    // Check token validity periodically (every 24 hours)
    if (this.tokenRefreshListener) {
      clearInterval(this.tokenRefreshListener);
    }

    this.tokenRefreshListener = setInterval(async () => {
      try {
        await this.checkAndRefreshToken();
      } catch (error) {
        console.error('Error in token refresh check:', error);
      }
    }, 24 * 60 * 60 * 1000); // 24 hours

    // Also check on page visibility change (when user returns to app)
    if (!this.visibilityHandlerBound) {
      document.addEventListener('visibilitychange', async () => {
        if (!document.hidden) {
          try {
            await this.checkAndRefreshToken();
          } catch (error) {
            console.error('Error checking token on visibility change:', error);
          }
        }
      });
      this.visibilityHandlerBound = true;
    }
  }

  readCandidateUrl(source) {
    if (!source) {
      return null;
    }

    if (typeof source === 'string') {
      return source;
    }

    const candidates = [
      source.url,
      source.link,
      source.click_action,
      source.clickAction,
      source.path,
      source.route,
    ];

    for (const candidate of candidates) {
      if (typeof candidate === 'string' && candidate.trim()) {
        return candidate;
      }
    }

    return null;
  }

  resolveNotificationUrl(data, payload) {
    const directUrl = this.readCandidateUrl(data);
    if (directUrl) {
      return directUrl;
    }

    const notificationDataUrl = this.readCandidateUrl(payload?.notification?.data);
    if (notificationDataUrl) {
      return notificationDataUrl;
    }

    const notificationUrl = this.readCandidateUrl(payload?.notification);
    if (notificationUrl) {
      return notificationUrl;
    }

    const optionsUrl = this.readCandidateUrl(payload?.fcmOptions);
    if (optionsUrl) {
      try {
        const url = new URL(optionsUrl, window.location.origin);
        return `${url.pathname}${url.search}${url.hash}`;
      } catch {
        return optionsUrl;
      }
    }

    return null;
  }

  /**
   * Check if token needs refresh and update if necessary
   */
  async checkAndRefreshToken() {
    const lastUpdate = AppConfig.getLocal('notification_token_timestamp');
    const now = Date.now();
    
    // Refresh if token is older than 30 days or if no timestamp exists
    if (!lastUpdate || (now - lastUpdate) > (30 * 24 * 60 * 60 * 1000)) {
      console.log('Token is old, refreshing...');
      try {
        await this.registerToken(true);
      } catch (error) {
        console.error('Failed to refresh token:', error);
      }
    }
  }

  /**
   * Unregister notification token
   */
  async unregisterToken() {
    const tokenId = AppConfig.getLocal('notification_token_id');
    
    if (!tokenId) {
      console.log('No token to unregister');
      return true;
    }

    try {
      const response = await apiClient.post('users/notification/unregister', {
        token_id: tokenId
      }, {}, { action: 'unregister_notification_token' });

      if (response.status === 200) {
        AppConfig.storeLocal('notification_enable', false);
        AppConfig.storeLocal('notification_token_id', '');
        AppConfig.storeLocal('notification_token_timestamp', '');
        this.currentToken = null;
        
        // Clear refresh listener
        if (this.tokenRefreshListener) {
          clearInterval(this.tokenRefreshListener);
          this.tokenRefreshListener = null;
        }
        
        console.log('Token unregistered successfully');
        return true;
      }
    } catch (error) {
      console.error('Error unregistering token:', error);
      // Still clear local storage even if backend call fails
      AppConfig.storeLocal('notification_enable', false);
      AppConfig.storeLocal('notification_token_id', '');
      AppConfig.storeLocal('notification_token_timestamp', '');
      throw error;
    }
  }

  /**
   * Handle foreground notifications
   */
  handleForegroundNotification(payload) {
    const { notification, data } = payload;
    const targetUrl = this.resolveNotificationUrl(data, payload);
    
    if (!notification) return;

    // Show browser notification if permission is granted
    if (Notification.permission === 'granted') {
      const notificationOptions = {
        body: notification.body,
        icon: notification.icon || '/logo.svg',
        badge: notification.badge || '/logo.svg',
        tag: notification.tag || 'default',
        data: data,
        requireInteraction: false
      };

      const browserNotification = new Notification(notification.title, notificationOptions);

      // Handle notification click
      browserNotification.onclick = (event) => {
        event.preventDefault();
        if (targetUrl) {
          window.location.href = targetUrl;
        }
        browserNotification.close();
      };
    }
  }

  /**
   * Check if notifications are enabled
   */
  isEnabled() {
    return AppConfig.getLocal('notification_enable') === true;
  }

  /**
   * Get current token ID
   */
  getTokenId() {
    return AppConfig.getLocal('notification_token_id');
  }
}

// Export singleton instance
const notificationService = new NotificationService();
export default notificationService;
